import { Fragment, useEffect, useRef } from "react";
import { PlotCard } from "@hamlak/ui";
import type { PlotCard as PlotCardData } from "@/api/types";
import { homeColumns, useWidthClass } from "@/app/layout/useWidthClass";
import { t } from "@/i18n/ko";
import { FeedbackEntry } from "@/components/FeedbackEntry";
import "./plotGrid.css";

type Props = {
  items: PlotCardData[];
  onSelect: (plot: PlotCardData, position: number) => void;
  hasNext: boolean;
  isFetchingNext: boolean;
  fetchNext: () => void;
};

/** 의견 배너는 카드 2줄 뒤 한 줄 전체(F02-01). 2줄이 안 되면 목록 끝(AC0-315). */
const FEEDBACK_AFTER_ROWS = 2;

/** 2열(compact·medium) ↔ 5열(expanded) · 6열(large). 열 수는 CSS 변수 --home-cols, data-cols는 검증용. 리마운트 없음. */
export function PlotGrid({
  items,
  onSelect,
  hasNext,
  isFetchingNext,
  fetchNext,
}: Props) {
  const widthClass = useWidthClass();
  const cols = homeColumns(widthClass);
  const feedbackAfter = Math.min(cols * FEEDBACK_AFTER_ROWS, items.length) - 1;
  const sentinel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasNext || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !isFetchingNext) fetchNext();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNext, isFetchingNext, fetchNext]);

  return (
    <>
      <div className="plot-grid" data-testid="plot-grid" data-cols={cols}>
        {items.map((plot, i) => (
          <Fragment key={plot.id}>
            <PlotCard
              coverUrl={plot.coverThumbUrl ?? plot.coverUrl}
              name={plot.name}
              tagline={plot.tagline}
              tags={plot.tags}
              totalMessageCount={plot.totalMessageCount}
              imageLoading="eager"
              {...(plot.coverAnimated ? { motionUrl: plot.coverUrl } : {})}
              aria-label={`${plot.name} · ${plot.tagline} · ${t("plot.conversations", { count: plot.totalMessageCount })}`}
              onClick={() => onSelect(plot, i)}
            />
            {i === feedbackAfter && <FeedbackEntry from="home" className="plot-grid__feedback" />}
          </Fragment>
        ))}
      </div>
      {hasNext && (
        <div ref={sentinel} className="plot-grid-sentinel" aria-hidden="true" />
      )}
    </>
  );
}

const SKELETON_ROWS = 3;

/** 첫 조회 중 자리 표시(카드 모양 그대로). 열 수만큼 3줄, 광택이 좌→우로 지나간다. */
export function PlotGridSkeleton() {
  const cols = homeColumns(useWidthClass());
  return (
    <div className="plot-grid" data-testid="plot-grid-skeleton" role="status" aria-label={t("common.loading")}>
      {Array.from({ length: cols * SKELETON_ROWS }, (_, i) => (
        <div key={i} className="plot-skeleton" aria-hidden="true">
          <span className="plot-skeleton__bar plot-skeleton__bar--name" />
          <span className="plot-skeleton__bar plot-skeleton__bar--tagline" />
          <span className="plot-skeleton__bar plot-skeleton__bar--meta" />
        </div>
      ))}
    </div>
  );
}
